const clamp = (value) => Math.min(1, Math.max(0, value));
const ease = (value) => { const t = clamp(value); return t * t * (3 - 2 * t); };
const STEP_START = .28;
const STEP_INTERVAL = 1.15;
const STEP_ARRIVAL = .5;

class BarrierTabletStory extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('.bw-tablet-feature');
    this.outro = this.querySelector('.bw-tablet-feature__outro');
    this.intro = this.querySelector('.bw-tablet-feature__intro-panel');
    this.steps = [...this.querySelectorAll('[data-tablet-step]')];
    this.pill = this.querySelector('.bw-tablet-feature__pill');
    this.header = document.querySelector('#header-group');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.video = this.querySelector('[data-tablet-video]');
    this.videoProgress = 0;
    if (this.video) {
      this.video.muted = true;
      this.video.addEventListener('loadeddata', () => {
        if (this.enabled) this.pill.setAttribute('data-video-ready', '');
        this.seekVideo();
      }, { signal });
      this.video.addEventListener('seeked', () => {
        if (this.enabled) this.pill.setAttribute('data-video-ready', '');
        // Coalesce rapid scrolling into the latest requested frame.
        this.seekVideo();
      }, { signal });
      this.video.addEventListener('error', () => this.pill.removeAttribute('data-video-ready'), { signal });
      this.videoObserver = new IntersectionObserver(([entry]) => {
        this.videoVisible = entry.isIntersecting;
        this.prepareVideo();
      }, { rootMargin: '300px 0px' });
      this.videoObserver.observe(this.stage);
    }
    this.queue = () => {
      if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.render(); });
    };
    this.measure = this.measure.bind(this);
    // Capture also catches Horizon's desktop .page-wrapper scroll container.
    document.addEventListener('scroll', this.queue, { passive: true, capture: true, signal });
    window.addEventListener('resize', this.measure, { passive: true, signal });
    this.motion.addEventListener('change', this.measure, { signal });
    document.addEventListener('shopify:section:select', this.measure, { signal });
    this.resizeObserver = new ResizeObserver(this.measure);
    this.resizeObserver.observe(this.outro);
    if (this.intro) this.resizeObserver.observe(this.intro);
    if (this.header) this.resizeObserver.observe(this.header);
    const cartBar = document.querySelector('.bw-cart-bar');
    if (cartBar) this.resizeObserver.observe(cartBar);
    document.fonts.ready.then(() => { if (this.isConnected) this.measure(); });
    this.measure();
  }

  measure() {
    this.top = this.header?.getBoundingClientRect().height || 0;
    this.style.setProperty('--bw-story-top', `${this.top}px`);
    this.introHeight = this.intro?.offsetHeight || 0;
    this.style.setProperty('--bw-story-intro-height', `${this.introHeight}px`);
    // Reserve only the closing copy and its padding, without a fixed-height empty area.
    this.style.setProperty('--bw-story-outro-height', `${this.outro.offsetHeight}px`);
    // Natural reading order remains the fallback for reduced motion and short viewports.
    this.enabled = !this.motion.matches && window.innerHeight >= 600 && !window.Shopify?.designMode;
    this.toggleAttribute('data-animated', this.enabled);
    this.style.setProperty('--bw-story-scene-height', `${this.introHeight + this.stage.offsetHeight}px`);
    this.prepareVideo();
    if (!this.enabled) {
      this.steps.forEach((step) => { step.style.removeProperty('opacity'); step.style.removeProperty('transform'); });
      return;
    }
    const stepsBox = this.querySelector('.bw-tablet-feature__steps');
    // The steps can be missing mid-render; the next resize or font load measures again.
    if (!stepsBox || !this.pill) return;
    const copy = stepsBox.getBoundingClientRect();
    const pill = this.pill.getBoundingClientRect();
    this.mobile = window.innerWidth < 750;
    this.entryX = this.mobile ? Math.min(112, window.innerWidth * .28) : pill.left + pill.width / 2 - (copy.left + copy.width / 2);
    const stepHeights = this.steps.map((step) => step.offsetHeight);
    // Mobile copy varies in length; give each paragraph the same breathing room.
    this.slotHeights = stepHeights.map((height) => (this.mobile ? height : Math.max(...stepHeights)) + 36);
    // Start lower in the scene, then build a stack upward without fading older copy.
    const available = stepsBox.offsetHeight;
    const stackHeight = this.slotHeights.reduce((sum, height) => sum + height, 0) - 36;
    const lastSlotHeight = this.slotHeights.at(-1);
    this.anchorY = Math.max(0, (available - stackHeight) / 2) + stackHeight + 36 - lastSlotHeight;
    this.anchorY = Math.min(this.anchorY, Math.max(0, available - lastSlotHeight));
    this.travel = this.offsetHeight - this.introHeight - this.stage.offsetHeight;
    this.render();
  }

  render() {
    if (!this.enabled || this.travel <= 0) return;
    const scrollProgress = clamp((this.top - this.getBoundingClientRect().top - this.introHeight) / this.travel);
    const progress = scrollProgress * 3.8;
    const lastArrival = STEP_START + (this.steps.length - 1) * STEP_INTERVAL + STEP_ARRIVAL;
    // The artwork is fully exposed around 75% of the clip; the rest is a hold.
    // Align that visual peak with the third point finishing its entrance.
    this.videoProgress = progress <= lastArrival
      ? clamp((progress - STEP_START) / (lastArrival - STEP_START)) * .75
      : .75 + clamp((progress - lastArrival) / (3.8 - lastArrival)) * .25;
    this.seekVideo();
    this.steps.forEach((step, index) => {
      const start = STEP_START + index * STEP_INTERVAL;
      const arrival = ease((progress - start) / STEP_ARRIVAL);
      let upwardDistance = 0;
      for (let next = index + 1; next < this.steps.length; next++) {
        upwardDistance += ease((progress - (STEP_START + next * STEP_INTERVAL) + .2) / STEP_ARRIVAL) * this.slotHeights[next - 1];
      }
      step.style.opacity = arrival.toFixed(3);
      step.style.transform = `translate3d(${((1 - arrival) * this.entryX).toFixed(2)}px, ${(this.anchorY - upwardDistance).toFixed(2)}px, 0)`;
    });
  }

  prepareVideo() {
    if (!this.video) return;
    if (!this.enabled) {
      this.pill.removeAttribute('data-video-ready');
      return;
    }
    if (this.videoVisible && !this.videoRequested) {
      this.videoRequested = true;
      this.loadScrollVideo();
    }
    if (this.video.readyState >= 2) this.pill.setAttribute('data-video-ready', '');
  }

  async loadScrollVideo() {
    const source = [...this.video.querySelectorAll('source')].find((item) => this.video.canPlayType(item.type));
    if (!source) return;
    try {
      // Load this small clip once so every frame is seekable, including on
      // preview servers that cannot serve partial video requests.
      const response = await fetch(source.src, { signal: this.controller.signal });
      if (!response.ok) throw new Error('Unable to load tablet video');
      const blob = await response.blob();
      if (!this.isConnected || this.controller.signal.aborted) return;
      this.videoObjectURL = URL.createObjectURL(blob);
      this.video.src = this.videoObjectURL;
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.video.src = source.src;
    }
    this.video.preload = 'auto';
    this.video.load();
  }

  seekVideo() {
    if (!this.video || !this.enabled || this.video.readyState < 2 || this.video.seeking) return;
    // Scrub the full five-second dissolve and hold the last fully revealed frame.
    const end = Math.max(0, this.video.duration - 1 / 24);
    const time = this.videoProgress * end;
    if (!Number.isFinite(time) || Math.abs(this.video.currentTime - time) <= 1 / 48) return;
    // A decoder may round the timestamp. Do not repeatedly seek the same target.
    if (this.lastSeekTime !== undefined && Math.abs(this.lastSeekTime - time) <= 1 / 48) return;
    this.lastSeekTime = time;
    this.video.currentTime = time;
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.videoObserver?.disconnect();
    this.video?.pause();
    if (this.videoObjectURL) URL.revokeObjectURL(this.videoObjectURL);
    cancelAnimationFrame(this.frame);
  }
}

if (!customElements.get('barrier-tablet-story')) customElements.define('barrier-tablet-story', BarrierTabletStory);
